// Dependency graph builder (ARCHITECTURE.md §7.2, §7.5).
//
// Pure function that layers project structure on top of the AST graph: it
// embeds the AstGraph, turns each AST file node into an artifact-aware
// DependencyNode, materialises an `artifact` node per referenced artifact, and
// merges AST edges with manifest edges. Finally it derives `boundary_crossing`
// edges (§7.5): whenever an AST edge connects files in different artifacts, an
// artifact→artifact edge records the contract-level relationship.
//
// No IO and no store interaction (orchestration lives in ./index.ts).

import type { AstGraph } from "../ast/types.js";
import type { GraphEdge } from "../types.js";
import { computeRelevanceScore } from "../evidence/index.js";
import type { DependencyGraph, DependencyNode } from "./types.js";

export interface BuildDependencyGraphInput {
  /** The AST graph whose file nodes and edges form the structural base. */
  ast: AstGraph;
  /** External-package edges from manifests (§7.3). */
  manifestEdges: GraphEdge[];
  /** filePath → artifact IDs (from the artifact mapper, §7.4). */
  artifactMapping: Map<string, string[]>;
}

/** Stable dedup key for an edge: (from, to, kind). */
function edgeKey(edge: GraphEdge): string {
  return `${edge.from} ${edge.to} ${edge.kind}`;
}

/** The file portion of a node id: the part before the first '#', or the id. */
function fileOf(nodeId: string): string {
  const hash = nodeId.indexOf("#");
  return hash >= 0 ? nodeId.slice(0, hash) : nodeId;
}

export interface ResolveSymbolEdgesInput {
  /** Edges to resolve; only `calls` / `type_reference` edges are touched. */
  edges: GraphEdge[];
  /** Symbol name → candidate symbol ids (a name may exist in several files). */
  symbolIndex: Map<string, string[]>;
  /** Every symbol node id, for same-file resolution. */
  symbolIds: Set<string>;
}

/**
 * Resolve `calls` / `type_reference` edges whose `to` is still a bare symbol
 * name into a concrete symbol id (§4.2). Resolution prefers a symbol declared
 * in the same file as the edge's source; otherwise it accepts a globally unique
 * match. Ambiguous or unknown names are left untouched so the edge still
 * records the (unresolved) reference. Edges already pointing at a symbol id
 * (containing '#') or of any other kind pass through unchanged.
 */
export function resolveSymbolEdges(input: ResolveSymbolEdgesInput): GraphEdge[] {
  const { edges, symbolIndex, symbolIds } = input;
  return edges.map((edge) => {
    if (edge.kind !== "calls" && edge.kind !== "type_reference") return edge;
    if (edge.to.includes("#")) return edge;

    // 1. Same-file symbol: `<fromFile>#<name>`.
    const sameFile = `${fileOf(edge.from)}#${edge.to}`;
    if (symbolIds.has(sameFile)) {
      return { ...edge, to: sameFile };
    }

    // 2. Globally unique symbol of that name.
    const candidates = symbolIndex.get(edge.to);
    if (candidates && candidates.length === 1) {
      return { ...edge, to: candidates[0] as string };
    }

    // 3. Ambiguous or unknown — leave the bare name in place.
    return edge;
  });
}

/**
 * Build a DependencyGraph from an AstGraph, manifest edges, and a file→artifact
 * mapping. Pure — no IO, no store.
 */
export function buildDependencyGraph(
  input: BuildDependencyGraphInput,
): DependencyGraph {
  const { ast, manifestEdges, artifactMapping } = input;

  const nodes = new Map<string, DependencyNode>();

  // 1. File and symbol nodes (§5): one DependencyNode per AST node. File nodes
  //    are enriched with their artifact ids; symbol nodes inherit the artifact
  //    ids of their containing file so symbol-level impact can resolve them.
  const symbolIndex = new Map<string, string[]>();
  const symbolIds = new Set<string>();
  for (const node of ast.nodes.values()) {
    if (node.kind === "file") {
      nodes.set(node.id, {
        id: node.id,
        kind: "file",
        metadata: node.metadata,
        artifactIds: artifactMapping.get(node.filePath) ?? [],
      });
    } else if (node.kind === "symbol") {
      nodes.set(node.id, {
        id: node.id,
        kind: "symbol",
        metadata: node.metadata,
        artifactIds: artifactMapping.get(node.filePath) ?? [],
      });
      symbolIds.add(node.id);
      const name = node.symbolInfo?.name;
      if (name) {
        const list = symbolIndex.get(name);
        if (list) list.push(node.id);
        else symbolIndex.set(name, [node.id]);
      }
    }
  }

  // Resolve `calls` / `type_reference` edges whose `to` is still a bare symbol
  // name into concrete symbol ids before they enter the graph (§4.2).
  const astEdges = resolveSymbolEdges({
    edges: ast.edges,
    symbolIndex,
    symbolIds,
  });

  // 2. Artifact nodes: one per artifact referenced by the mapping.
  for (const artifactIds of artifactMapping.values()) {
    for (const artifactId of artifactIds) {
      if (nodes.has(artifactId)) continue;
      nodes.set(artifactId, {
        id: artifactId,
        kind: "artifact",
        metadata: {},
        artifactIds: [artifactId],
      });
    }
  }

  // 3. Merge AST edges + manifest edges, deduplicated by (from, to, kind).
  const edges: GraphEdge[] = [];
  const seen = new Set<string>();
  for (const edge of [...astEdges, ...manifestEdges]) {
    const key = edgeKey(edge);
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push(edge);
  }

  // 4. Boundary-crossing edges (§7.5): for every AST edge whose endpoints are
  //    files in different artifacts, record an artifact→artifact edge.
  for (const edge of astEdges) {
    const fromArtifacts = artifactMapping.get(edge.from);
    const toArtifacts = artifactMapping.get(edge.to);
    if (!fromArtifacts || !toArtifacts) continue;

    for (const from of fromArtifacts) {
      for (const to of toArtifacts) {
        if (from === to) continue;
        const boundaryEdge: GraphEdge = {
          from,
          to,
          kind: "boundary_crossing",
          classification: "deterministic",
          evidence: [
            {
              type: "path_pattern_match",
              detail: `boundary_crossing ${from} -> ${to} via ${edge.from} -> ${edge.to}`,
              confidence: 1.0,
            },
          ],
        };
        const key = edgeKey(boundaryEdge);
        if (seen.has(key)) continue;
        seen.add(key);
        edges.push(boundaryEdge);
      }
    }
  }

  // 5. Derive each edge's deterministic relevance score from its evidence
  //    (Issue #21). Edges are rebuilt as fresh objects so shared inputs
  //    (ast.edges, manifestEdges) are never mutated.
  const scoredEdges = edges.map((edge) => ({
    ...edge,
    relevanceScore: computeRelevanceScore(edge.evidence),
  }));

  return {
    ast,
    nodes,
    edges: scoredEdges,
    artifactMapping: new Map(artifactMapping),
  };
}
